/**
 * ActivationContext — single source of truth for "what state is the
 * current authenticated user in".
 *
 * Locked 2026-06-01 — Onboarding Consolidation Phase A (Step 1).
 *
 * Reads the canonical /api/auth/me endpoint on:
 *   - mount
 *   - route change
 *   - window focus (catches state changes from other tabs)
 *
 * Exposes:
 *   {
 *     loading,
 *     authenticated,          // true iff /auth/me returned 200
 *     userId, role, companyId, email, fullName,
 *     activationState,        // 6-value enum, source of truth
 *     signupResumeStep,       // legacy 4-value, kept for back-compat
 *     walkthroughKind,        // 'provider' | 'delegated' | 'none'
 *     walkthroughEligible,    // bool
 *     walkthroughVersion,
 *     walkthroughProgress,
 *     emailVerified, termsAccepted, subscriptionActive,
 *     onboardingStarted, onboardingCompleted, firstLoginComplete,
 *     refresh,                // force a re-poll
 *   }
 *
 * This context REPLACES the scattered localStorage flag reads
 * (hasCompletedOnboarding / terms_accepted / first_login_pending /
 * signup_in_progress / walkthrough_completed:{uid} / etc.) for
 * routing decisions. localStorage still works as a paint-time cache
 * for first render, but every routing decision flows through this
 * context, which always re-reads from the backend on navigation.
 *
 * The context is null-safe: callers can use `useActivation()` even
 * before the first response and get `loading: true` + sensible
 * defaults. Public/auth/super-admin routes can simply not mount the
 * provider — the hook degrades gracefully.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_STATE = {
  loading: true,
  authenticated: false,
  userId: null,
  role: null,
  companyId: null,
  email: null,
  fullName: null,
  activationState: null,
  signupResumeStep: null,
  walkthroughKind: 'none',
  walkthroughEligible: false,
  walkthroughVersion: 0,
  walkthroughProgress: null,
  emailVerified: false,
  termsAccepted: false,
  subscriptionActive: false,
  onboardingStarted: false,
  onboardingCompleted: false,
  firstLoginComplete: false,
};

const ActivationContext = createContext({
  ...DEFAULT_STATE,
  refresh: () => {},
});

export const useActivation = () => useContext(ActivationContext);

const _resolveToken = () => {
  try {
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token') ||
      null
    );
  } catch {
    return null;
  }
};

export const ActivationProvider = ({ children }) => {
  const location = useLocation();
  const [state, setState] = useState(DEFAULT_STATE);
  const lastFetchRef = useRef(0);

  const fetchMe = useCallback(async () => {
    const token = _resolveToken();
    if (!token) {
      setState({ ...DEFAULT_STATE, loading: false, authenticated: false });
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data || {};
      setState({
        loading: false,
        authenticated: true,
        userId: d.user_id || null,
        role: d.role || null,
        companyId: d.company_id || null,
        email: d.email || null,
        fullName: d.full_name || null,
        activationState: d.activation_state || null,
        signupResumeStep: d.signup_resume_step || null,
        walkthroughKind: d.walkthrough_kind || 'none',
        walkthroughEligible: !!d.walkthrough_eligible,
        walkthroughVersion: d.walkthrough_version || 0,
        walkthroughProgress: d.walkthrough_progress || null,
        emailVerified: !!d.email_verified,
        termsAccepted: !!d.terms_accepted,
        subscriptionActive: !!d.subscription_active,
        onboardingStarted: !!d.onboarding_started,
        onboardingCompleted: !!d.onboarding_completed,
        firstLoginComplete: !!d.first_login_complete,
      });
      lastFetchRef.current = Date.now();
    } catch (err) {
      // 401 = no/bad token → unauthenticated, treat as logged-out.
      // Any other error → keep last known state but mark loaded so
      // routing isn't blocked indefinitely on a network blip.
      const status = err?.response?.status;
      if (status === 401 || status === 404) {
        setState({ ...DEFAULT_STATE, loading: false, authenticated: false });
      } else {
        setState((s) => ({ ...s, loading: false }));
      }
    }
  }, []);

  // Initial fetch + on route change.
  useEffect(() => {
    fetchMe();
  }, [fetchMe, location.pathname]);

  // Refresh on window focus (catches cross-tab logins/logouts).
  useEffect(() => {
    const onFocus = () => {
      // Throttle: don't refetch if we just fetched within 5s.
      if (Date.now() - lastFetchRef.current < 5000) return;
      fetchMe();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchMe]);

  const value = { ...state, refresh: fetchMe };

  return (
    <ActivationContext.Provider value={value}>
      {children}
    </ActivationContext.Provider>
  );
};

export default ActivationProvider;
